import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selectCls = inputCls;
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";

const EMPTY_FORM = {
  name: '', address: '', phone: '', email: '',
  region_id: '', latitude: '', longitude: '', is_active: true,
};

export default function Companies() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // mobile expand

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const fetchAll = useCallback(async () => {
    try {
      const [companiesRes, regionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies`, { headers: authHeader }),
        axios.get(`${BACKEND_URL}/api/regions`, { headers: authHeader }),
      ]);
      setCompanies(companiesRes.data || []);
      setRegions(regionsRes.data || []);
    } catch { toast.error('Gabim gjate ngarkimit'); }
  }, []);

  useEffect(() => {
    const init = async () => { setLoading(true); await fetchAll(); setLoading(false); };
    init();
  }, []);

  const openAdd = () => { setEditingCompany(null); setForm(EMPTY_FORM); setShowForm(true); setTimeout(() => document.getElementById('company-name-input')?.focus(), 50); };
  const openEdit = (c) => {
    setEditingCompany(c);
    setForm({ name: c.name||'', address: c.address||'', phone: c.phone||'', email: c.email||'', region_id: c.region_id?String(c.region_id):'', latitude: c.latitude??'', longitude: c.longitude??'', is_active: c.is_active!==false });
    setShowForm(true); setExpandedId(null); setTimeout(() => document.getElementById('company-name-input')?.focus(), 50);
  };
  const cancelForm = () => { setShowForm(false); setEditingCompany(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Emri eshte i detyrueshem'); return; }
    setSaving(true);
    try {
      if (editingCompany) {
        await axios.put(`${BACKEND_URL}/api/companies/${editingCompany.id}`, form, { headers: authHeader });
        toast.success('Kompania u perditesua');
      } else {
        await axios.post(`${BACKEND_URL}/api/companies`, form, { headers: authHeader });
        toast.success('Kompania u krijua');
      }
      cancelForm(); await fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Gabim'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`A jeni te sigurt qe doni te fshini "${c.name}"?`)) return;
    setDeletingId(c.id);
    try { await axios.delete(`${BACKEND_URL}/api/companies/${c.id}`, { headers: authHeader }); toast.success('Kompania u fshi'); setExpandedId(null); await fetchAll(); }
    catch { toast.error('Gabim'); }
    finally { setDeletingId(null); }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const regionName = (id) => regions.find(r => String(r.id) === String(id))?.name || '–';

  return (
    <Layout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Menaxhimi i Kompanive</h1>
          {!showForm && (
            <button onClick={openAdd} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition">
              + Shto Kompani
            </button>
          )}
        </div>

        {/* ── Forma ── */}
        {showForm && (
          <div className={`${cardCls} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {editingCompany ? 'Ndrysho Kompanine' : 'Shto Kompani te Re'}
              </h2>
              <button type="button" onClick={cancelForm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Emri *</label>
                  <input id="company-name-input" type="text" value={form.name} onChange={f('name')} className={inputCls} required placeholder="Emri i kompanise" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Adresa</label>
                  <input type="text" value={form.address} onChange={f('address')} className={inputCls} placeholder="Adresa" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Telefoni</label>
                  <input type="text" value={form.phone} onChange={f('phone')} className={inputCls} placeholder="+383..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={f('email')} className={inputCls} placeholder="email@kompania.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Rajoni</label>
                  <select value={form.region_id} onChange={f('region_id')} className={selectCls}>
                    <option value="">-- Zgjidhni --</option>
                    {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Gjeresia (Lat)</label>
                  <input type="number" step="any" value={form.latitude} onChange={f('latitude')} className={inputCls} placeholder="42.6026" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Gjatesia (Lng)</label>
                  <input type="number" step="any" value={form.longitude} onChange={f('longitude')} className={inputCls} placeholder="20.9030" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Statusi</label>
                  <select value={form.is_active ? 'active' : 'inactive'} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === 'active' }))} className={selectCls}>
                    <option value="active">Aktive</option>
                    <option value="inactive">Joaktive</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                  {saving ? 'Duke ruajtur...' : (editingCompany ? 'Perditeso' : 'Shto Kompani')}
                </button>
                <button type="button" onClick={cancelForm} className="px-5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition">
                  Anulo
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">Duke ngarkuar...</div>
        ) : companies.length === 0 ? (
          <div className={`${cardCls} p-8 text-center text-gray-500 dark:text-gray-400 text-sm`}>
            Asnje kompani. {!showForm && <button onClick={openAdd} className="text-blue-600 hover:underline">Shto kompanine e pare</button>}
          </div>
        ) : (
          <>
            {/* ══════════════════════════════════
                MOBILE: karta me expand (⋯ more)
                ══════════════════════════════════ */}
            <div className="md:hidden space-y-2">
              {companies.map(c => {
                const isOpen = expandedId === c.id;
                const region = regionName(c.region_id);
                const active = c.is_active !== false;
                return (
                  <div key={c.id} className={`${cardCls} overflow-hidden transition-all`}>
                    {/* ─ Rreshti kryesor ─ */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      {/* Avatar shkronja */}
                      <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                          {c.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      {/* Emri + rajoni */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{c.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {region !== '–' && (
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                              </svg>
                              {region}
                            </span>
                          )}
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {active ? '● Aktive' : '○ Joaktive'}
                          </span>
                        </div>
                      </div>
                      {/* Butoni ⋯ */}
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : c.id)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition flex-shrink-0 ${
                          isOpen
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="5" cy="12" r="1.5"/>
                          <circle cx="12" cy="12" r="1.5"/>
                          <circle cx="19" cy="12" r="1.5"/>
                        </svg>
                      </button>
                    </div>

                    {/* ─ Expand: detaje + veprime ─ */}
                    {isOpen && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-2 bg-gray-50 dark:bg-gray-800/60">
                        {c.address && (
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span className="text-sm text-gray-600 dark:text-gray-300">{c.address}</span>
                          </div>
                        )}
                        {c.phone && (
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="text-sm text-gray-600 dark:text-gray-300">{c.phone}</span>
                          </div>
                        )}
                        {c.email && (
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{c.email}</span>
                          </div>
                        )}
                        {/* Veprimet */}
                        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <button
                            onClick={() => openEdit(c)}
                            className="flex-1 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                          >
                            ✏️ Ndrysho
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            disabled={deletingId === c.id}
                            className="flex-1 py-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition disabled:opacity-50"
                          >
                            {deletingId === c.id ? '...' : '🗑️ Fshi'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ══════════════════════════════════
                DESKTOP: tabela klasike
                ══════════════════════════════════ */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      {['Emri','Adresa','Telefoni','Email','Rajoni','Statusi','Veprimet'].map(h => (
                        <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {companies.map(c => (
                      <tr key={c.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${editingCompany?.id===c.id ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                        <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{c.name}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{c.address||'–'}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{c.phone||'–'}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{c.email||'–'}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{regionName(c.region_id)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${c.is_active!==false ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {c.is_active!==false ? 'Aktive' : 'Joaktive'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-2">
                            <button onClick={() => openEdit(c)} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg transition">Ndrysho</button>
                            <button onClick={() => handleDelete(c)} disabled={deletingId===c.id} className="px-3 py-1 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded-lg transition disabled:opacity-50">
                              {deletingId===c.id ? '...' : 'Fshi'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
